// ─── El deep link `/admin/notifications?notification=<id>` ───────────────────
//
// El modal de Inspección de Descarga enlaza cada cruce de geocerca con la
// notificación que ese cruce disparó. Acá vive lo decidible de ese enlace —a
// qué URL va y qué se hace con cada respuesta al aterrizar—, sin React ni
// axios, para poder fijarlo con tests: `NotificationsSection` arrastra
// `utils/api` → firebase y en un worktree sin `apps/v5/.env` no se puede montar.
//
// Por qué se trae POR ID en vez de buscarla en la lista (que es lo que tuvo que
// hacer el historial de inspecciones, ver `deepLinkWiden.ts`): acá sí existe un
// detalle por id, `GET /notifications?notification_id=`, y la lista es el peor
// lugar para buscar — `/notificationsList` pagina de a 10 sobre TODAS las
// notificaciones del usuario ordenadas por fecha, y un patio con tráfico emite
// dos por visita, así que la de un cruce de ayer ya está varias páginas atrás.

/** El query param. Uno solo, como `?event=` en accesos y `?inspection=` en el historial. */
export const NOTIFICATION_PARAM = "notification";

/** La ruta de la sección (router/config.tsx: `/admin` → `notifications`). */
export const NOTIFICATIONS_PATH = "/admin/notifications";

/**
 * La URL que abre el detalle de esa notificación, o `null` si no hay id con el
 * que armarla. `null` y no la ruta pelada: un enlace que lleva a la lista
 * general promete "esta notificación" y entrega cuatrocientas.
 */
export function notificationDeepLinkPath(
  notificationId: string | null | undefined,
): string | null {
  const id = typeof notificationId === "string" ? notificationId.trim() : "";
  if (!id) return null;
  return `${NOTIFICATIONS_PATH}?${NOTIFICATION_PARAM}=${encodeURIComponent(id)}`;
}

/** Los campos de `Notification` que deciden si el usuario la puede ver. */
export interface NotificationAudience {
  type_of_addresse?: string | null;
  addresses?: unknown;
  addressee?: string | null;
  createdBy?: string | null;
}

/**
 * ¿La sección le mostraría esta notificación a este usuario?
 *
 * Es el MISMO criterio con el que el backend arma `/notificationsList`
 * (`userIsAddressee(...) || createdBy === userId` en notificationsController.js)
 * y tiene que seguir siéndolo, por una razón concreta: el detalle por id NO
 * valida destinatario — sólo token, sesión y scope de lectura — así que
 * devuelve cualquier notificación a cualquier usuario autenticado. Las de
 * geocerca son dirigidas (`type_of_addresse: "specific"`, `addresses: [...]`):
 * sin este filtro el deep link abriría, con sólo conocer un id, lo que la
 * lista de la misma pantalla esconde.
 *
 * Esto NO es un control de acceso —el documento ya viajó al navegador—; es que
 * la pantalla no contradiga su propia lista. El control va en el backend.
 */
export function isNotificationVisibleTo(
  notification: NotificationAudience,
  userId: string | null | undefined,
): boolean {
  const uid = typeof userId === "string" ? userId.trim() : "";
  if (!uid) return false;
  if (notification.type_of_addresse === "all") return true;
  if (notification.createdBy === uid) return true;
  const { addresses } = notification;
  if (addresses != null) {
    const list = Array.isArray(addresses) ? addresses : [addresses];
    if (list.some((addr) => String(addr).trim() === uid)) return true;
  }
  if (notification.addressee) {
    const addr = String(notification.addressee).trim();
    return addr === uid || addr === "all";
  }
  return false;
}

/** Lo que devolvió el detalle por id, ya sin axios. */
export type NotificationFetchResult<T> =
  | { ok: true; notification: T | null | undefined }
  /** `status` es el HTTP de la respuesta; ausente si no hubo respuesta (red). */
  | { ok: false; status?: number };

export type NotificationDeepLinkReason =
  /** No es destinatario ni autor, o el backend contestó 401/403. */
  | "forbidden"
  /** 404, o un 200 sin documento: borrada o id inventado. */
  | "notFound"
  /** Red caída o 5xx: no se sabe si existe. */
  | "failed";

export type NotificationDeepLinkAction<T> =
  | { type: "open"; notification: T }
  | { type: "message"; reason: NotificationDeepLinkReason };

/**
 * Qué hacer con la respuesta del detalle. Pura.
 *
 * No hay acción "reintentar" ni "esperar", y es deliberado: las dos salidas
 * son TERMINALES y en las dos quien llama limpia el param. Dejarlo puesto ante
 * un fallo es lo que arma el bucle — el efecto depende de `searchParams`, y con
 * el id todavía en la URL cada render vuelve a pedir una notificación que va a
 * volver a fallar.
 *
 * "No existe" y "no es tuya" se dicen por separado porque el usuario hace
 * cosas distintas con cada una: ante la primera no hay nada que pedir; ante la
 * segunda se la pide a quien sí la recibió.
 */
export function resolveNotificationDeepLink<T extends NotificationAudience>(
  result: NotificationFetchResult<T>,
  userId: string | null | undefined,
): NotificationDeepLinkAction<T> {
  if (!result.ok) {
    if (result.status === 404) return { type: "message", reason: "notFound" };
    if (result.status === 401 || result.status === 403) {
      return { type: "message", reason: "forbidden" };
    }
    return { type: "message", reason: "failed" };
  }
  const { notification } = result;
  if (!notification) return { type: "message", reason: "notFound" };
  if (!isNotificationVisibleTo(notification, userId)) {
    return { type: "message", reason: "forbidden" };
  }
  return { type: "open", notification };
}

/** El texto de cada salida sin modal. Es el respaldo de las claves de i18n. */
export const NOTIFICATION_DEEP_LINK_MESSAGES: Record<
  NotificationDeepLinkReason,
  { key: string; title: string; text: string }
> = {
  forbidden: {
    key: "notifications.deepLink.forbidden",
    title: "No tienes acceso a esta notificación",
    text: "Sólo la pueden ver sus destinatarios y quien la envió.",
  },
  notFound: {
    key: "notifications.deepLink.notFound",
    title: "La notificación ya no existe",
    text: "Puede que la hayan eliminado o que el enlace esté incompleto.",
  },
  failed: {
    key: "notifications.deepLink.failed",
    title: "No se pudo abrir la notificación",
    text: "Revisa tu conexión y vuelve a abrir el enlace.",
  },
};
